import { hyp } from '../../core/exactMath';
import type { GridPoint } from '../../core/types';
import type { WorldMap } from '../../world/WorldMap';
import type { BuildingStore } from '../buildings/BuildingStore';
import { buildingDef, type BuildingTypeId } from '../buildings/definitions';
import type { Colony } from '../colony/Colony';
import type { Economy } from '../economy/Economy';
import type { InfectedDirector } from '../infected/InfectedDirector';
import type { PlayerId } from '../ids';
import type { UnitStore } from '../units/UnitStore';

/**
 * Campaign objectives beyond "survive" (D124): Infected Nests to destroy, and
 * Survivor Camps to reach. Placed deterministically when the match starts,
 * stepped on sim ticks, counted by MatchRules for victory.
 */
export interface ObjectivesConfig {
  readonly nests?: { readonly count: number; readonly spawnEvery: number; readonly perSpawn: number; readonly guards?: number };
  readonly camps?: { readonly count: number; readonly survivorsEach: number; readonly supplies: number; readonly guards?: number; readonly holdSeconds?: number };
}

export type ObjectiveEvent =
  | { type: 'rescued'; buildingId: number; by: PlayerId; survivors: number; at: GridPoint }
  | { type: 'nestSpawned'; buildingId: number; count: number; raid: boolean }
  | { type: 'rescuing'; buildingId: number; progress: number };

const RESCUE_RADIUS = 2.6;
/** cos / sin of −0.8 … +0.8 rad in 0.2 steps, as literals. */
const FAN: readonly (readonly [number, number])[] = [
  [0.696707, -0.717356],
  [0.825336, -0.564642],
  [0.921061, -0.389418],
  [0.980067, -0.198669],
  [1.000000, 0.000000],
  [0.980067, 0.198669],
  [0.921061, 0.389418],
  [0.825336, 0.564642],
  [0.696707, 0.717356],
];
/** A nest stops breeding while this many of the infected are already around it. */
const NEST_CAP = 10;
/** Every Nth brood marches on your Command Center; the rest stay and guard the nest. */
const RAID_EVERY = 4;

export class Objectives {
  private time = 0;
  private nextSpawn = new Map<number, number>();
  private rescuedCount = 0;
  private readonly broods = new Map<number, number>();
  private readonly holding = new Map<number, number>();
  private readonly totalNests: number;
  private readonly listeners = new Set<(e: ObjectiveEvent) => void>();

  constructor(
    private readonly cfg: ObjectivesConfig,
    private readonly world: WorldMap,
    private readonly buildings: BuildingStore,
    private readonly units: UnitStore,
    private readonly infected: InfectedDirector,
    private readonly colony: Colony,
    private readonly economy: Economy,
    private readonly player: PlayerId,
  ) {
    const home = this.homeOf(player);
    if (cfg.nests) {
      for (const at of this.spots('infected_nest', cfg.nests.count, home, 0.42)) {
        const b = this.buildings.add('infected_nest', at, 'infected', { complete: true, preplaced: true });
        this.nextSpawn.set(b.id, cfg.nests.spawnEvery);
        if (cfg.nests.guards) this.infected.spawnAt({ x: at.x + 1, y: at.y + 3 }, cfg.nests.guards);
      }
    }
    if (cfg.camps) {
      for (const at of this.spots('survivor_camp', cfg.camps.count, home, 0.6)) {
        this.buildings.add('survivor_camp', at, 'neutral', { complete: true, preplaced: true });
        if (cfg.camps.guards) this.infected.spawnAt({ x: at.x + 1, y: at.y + 4 }, cfg.camps.guards);
      }
    }
    this.totalNests = this.nestsLeft;
  }

  subscribe(l: (e: ObjectiveEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  get nestsLeft(): number {
    return this.buildings.all().filter((b) => b.type === 'infected_nest').length;
  }

  get nestsTotal(): number {
    return this.totalNests;
  }

  get rescued(): number {
    return this.rescuedCount;
  }

  get campsTotal(): number {
    return this.cfg.camps?.count ?? 0;
  }

  step(dt: number): void {
    this.time += dt;
    const nests = this.cfg.nests;
    if (nests) {
      for (const b of this.buildings.all()) {
        if (b.type !== 'infected_nest') continue;
        const due = this.nextSpawn.get(b.id) ?? nests.spawnEvery;
        if (this.time < due) continue;
        this.nextSpawn.set(b.id, this.time + nests.spawnEvery);
        const c = { x: b.origin.x + 1, y: b.origin.y + 1 };
        const brood = (this.broods.get(b.id) ?? 0) + 1;
        this.broods.set(b.id, brood);
        const raid = brood % RAID_EVERY === 0;
        const around = this.units.all().filter((u) => u.ownerId === 'infected' && hyp(u.x - c.x, u.y - c.y) <= 8).length;
        if (!raid && around >= NEST_CAP) continue;
        const ids = this.infected.spawnAt(c, nests.perSpawn);
        const target = this.homeOf(this.player);
        if (raid && ids.length && target) this.units.attackMove(ids, target);
        if (ids.length) this.emit({ type: 'nestSpawned', buildingId: b.id, count: ids.length, raid });
      }
    }
    const camps = this.cfg.camps;
    if (camps) {
      for (const b of this.buildings.all()) {
        if (b.type !== 'survivor_camp') continue;
        const cx = b.origin.x + 1;
        const cy = b.origin.y + 1;
        const near = this.units.ownedBy(this.player).some((u) => hyp(u.x - cx, u.y - cy) <= RESCUE_RADIUS);
        // Someone has to stay with them a few seconds (packing up) — and the infected nearby must not be in the way.
        const threatened = this.units.enemiesNear(this.player, cx, cy, 4).length > 0;
        if (!near || threatened) {
          if (this.holding.has(b.id)) this.holding.delete(b.id);
          continue;
        }
        const held = (this.holding.get(b.id) ?? 0) + dt;
        this.holding.set(b.id, held);
        const need = camps.holdSeconds ?? 6;
        if (held < need) {
          this.emit({ type: 'rescuing', buildingId: b.id, progress: held / need });
          continue;
        }
        this.holding.delete(b.id);
        this.buildings.remove(b.id);
        const came = this.colony.returnSurvivors(this.player, camps.survivorsEach);
        const ledger = this.economy.ledger(this.player);
        ledger.add('wood', camps.supplies);
        ledger.add('scrap', Math.round(camps.supplies / 2));
        ledger.add('food', camps.supplies);
        this.economy.markChanged(this.player);
        this.rescuedCount++;
        this.emit({ type: 'rescued', buildingId: b.id, by: this.player, survivors: came, at: { x: cx, y: cy } });
      }
    }
  }

  snapshot(): { time: number; rescued: number; nextSpawn: Record<string, number>; broods: Record<string, number>; holding: Record<string, number> } {
    const rec = (m: Map<number, number>) => Object.fromEntries([...m].sort((a, b) => a[0] - b[0]).map(([k, v]) => [String(k), v]));
    return { time: this.time, rescued: this.rescuedCount, nextSpawn: rec(this.nextSpawn), broods: rec(this.broods), holding: rec(this.holding) };
  }

  /** Rescue progress 0..1 per camp being packed up (for the HUD). */
  holdProgress(buildingId: number): number {
    const need = this.cfg.camps?.holdSeconds ?? 6;
    return Math.min(1, (this.holding.get(buildingId) ?? 0) / need);
  }

  // ------------------------------------------------------------------

  private homeOf(p: PlayerId): GridPoint | null {
    const cc = this.buildings.ownedBy(p).find((b) => b.type === 'command_center');
    return cc ? { x: cc.origin.x + 1, y: cc.origin.y + 1 } : null;
  }

  private emit(e: ObjectiveEvent): void {
    for (const l of this.listeners) l(e);
  }

  /**
   * `count` free sites for a building, spread around the map at roughly
   * `reach` × the map size from the player's base (deterministic angles).
   */
  private reachable: Uint8Array | null = null;

  /** Tiles a unit can walk to from the base (flood fill, once) — objectives are never placed on islands. */
  private reach(home: GridPoint): Uint8Array {
    if (this.reachable) return this.reachable;
    const w = this.world.width;
    const h = this.world.height;
    const seen = new Uint8Array(w * h);
    const queue: number[] = [];
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const x = home.x + dx;
        const y = home.y + dy;
        if (x >= 0 && y >= 0 && x < w && y < h && this.world.isWalkable(x, y) && !this.buildings.at(x, y)) {
          seen[y * w + x] = 1;
          queue.push(y * w + x);
        }
      }
    }
    for (let i = 0; i < queue.length; i++) {
      const k = queue[i]!;
      const x = k % w;
      const y = (k - x) / w;
      for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]] as const) {
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const nk = ny * w + nx;
        if (seen[nk] || !this.world.isWalkable(nx, ny)) continue;
        seen[nk] = 1;
        queue.push(nk);
      }
    }
    this.reachable = seen;
    return seen;
  }

  private spots(type: BuildingTypeId, count: number, home: GridPoint | null, reach: number): GridPoint[] {
    const out: GridPoint[] = [];
    if (!home || count <= 0) return out;
    this.reach(home);
    const fp = buildingDef(type).footprint;
    const mid = { x: this.world.width / 2, y: this.world.height / 2 };
    // Direction to the map centre, fanned out by fixed rotations (no trig at runtime: cross-engine determinism, D90).
    const len = hyp(mid.x - home.x, mid.y - home.y) || 1;
    const ux = (mid.x - home.x) / len;
    const uy = (mid.y - home.y) / len;
    const dist = Math.min(this.world.width, this.world.height) * reach;
    for (let i = 0; i < count; i++) {
      const step = count === 1 ? 4 : Math.round((i / (count - 1)) * 8);
      const [c, sn] = FAN[step]!;
      const dx = ux * c - uy * sn;
      const dy = ux * sn + uy * c;
      const want = { x: Math.round(home.x + dx * dist), y: Math.round(home.y + dy * dist) };
      const at = this.freeNear(want, fp, out);
      if (at) out.push(at);
    }
    return out;
  }

  private freeNear(want: GridPoint, fp: { width: number; height: number }, taken: readonly GridPoint[]): GridPoint | null {
    for (let r = 0; r <= 10; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const o = { x: want.x + dx, y: want.y + dy };
          if (o.x < 1 || o.y < 1 || o.x + fp.width >= this.world.width - 1 || o.y + fp.height >= this.world.height - 1) continue;
          if (taken.some((t) => Math.abs(t.x - o.x) < 4 && Math.abs(t.y - o.y) < 4)) continue;
          let ok = true;
          for (let y = o.y; y < o.y + fp.height && ok; y++) {
            for (let x = o.x; x < o.x + fp.width && ok; x++) {
              if (!this.world.isWalkable(x, y) || this.buildings.at(x, y) || this.units.unitsOnTiles([{ x, y }]).length) ok = false;
            }
          }
          // At least one tile next to it must be walkable from the base.
          if (ok && this.reachable) {
            const w = this.world.width;
            let door = false;
            for (let x = o.x - 1; x <= o.x + fp.width && !door; x++) {
              for (const y of [o.y - 1, o.y + fp.height]) if (x >= 0 && y >= 0 && x < w && y < this.world.height && this.reachable[y * w + x]) door = true;
            }
            for (let y = o.y; y < o.y + fp.height && !door; y++) {
              for (const x of [o.x - 1, o.x + fp.width]) if (x >= 0 && y >= 0 && x < w && y < this.world.height && this.reachable[y * w + x]) door = true;
            }
            ok = door;
          }
          if (ok) return o;
        }
      }
    }
    return null;
  }
}
